import { useReducer, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import axiosInstance from 'utils/axiosConfig';
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from 'constants';
import { useAuth } from 'context/AuthContext';
import { useSession } from './useSession';
import { setAccessToken } from 'utils/tokenManager';
import {
  validateEmail,
  isSuperAdmin,
  getInstallSource,
  getDomainsFromResponse,
  getUserRole,
} from '../utils';
import { LOADING_STATES, STORAGE_KEYS } from '../constants';
import { completePendingOAuth } from '../oauthApprove';

const initialState = {
  currentStep: 'email',
  loadingState: LOADING_STATES.IDLE,
  email: '',
  otp: '',
  password: '',
  rememberMe: false,
  snackbar: { open: false, message: '', severity: 'success' },
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_LOADING':
      return { ...state, loadingState: action.payload };
    case 'SET_EMAIL':
      return { ...state, email: action.payload };
    case 'SET_OTP':
      return { ...state, otp: action.payload };
    case 'SET_PASSWORD':
      return { ...state, password: action.payload };
    case 'SET_REMEMBER_ME':
      return { ...state, rememberMe: action.payload };
    case 'SHOW_SNACKBAR':
      return {
        ...state,
        snackbar: { open: true, message: action.message, severity: action.severity ?? 'success' },
      };
    case 'HIDE_SNACKBAR':
      return { ...state, snackbar: { ...state.snackbar, open: false } };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
}


const authApi = {
  signup: (email, signal) =>
    axiosInstance.post(
      `${FAST_API_BASE_URL}/api/signup`,
      { email },
      { headers: { 'X-API-KEY': process.env.REACT_APP_API_KEY }, signal },
    ),

  verifyOtp: (email, otp, signal) =>
    axiosInstance.post(
      `${FAST_API_BASE_URL}/api/verify-otp`,
      { email, otp },
      { headers: { 'X-API-KEY': process.env.REACT_APP_API_KEY }, signal },
    ),

  resetPassword: (email) =>
    axiosInstance.post(`${FAST_API_BASE_URL}/api/reset-password`, { email }),

  saveGoogleUser: (decoded, referralCode, signal) =>
    axiosInstance.post(
      `${FAST_API_BASE_URL}/api/save-google-user`,
      {
        email: decoded.email,
        name: decoded.name,
        profile_pic: decoded.picture,
        referral_code: referralCode || null,
      },
      { headers: { 'X-API-KEY': process.env.REACT_APP_API_KEY }, signal },
    ),

  savePassword: (email, password, userName, referralCode) =>
    axiosInstance.post(
      `${FAST_API_BASE_URL}/api/save-password`,
      {
        password,
        email,
        user_name: userName,
        role: 'admin',
        referral_code: referralCode || null,
      },
      { headers: { 'Content-Type': 'application/json' } },
    ),

  login: (payload, headers, signal) =>
    axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
      headers,
      signal,
      withCredentials: true,
    }),

  reLogin: (payload, signal) =>
    axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, payload, {
      signal,
      withCredentials: true,
    }),

  register: (payload) =>
    axiosInstance.post(`${FAST_API_BASE_URL}/api/register`, payload, {
      headers: { 'X-API-KEY': process.env.REACT_APP_API_KEY },
    }),

  saveRegistration: (payload) =>
    axios.post(`${LAMBDA_BASE_URL}/save-registration`, payload, {
      headers: { 'X-API-KEY': process.env.REACT_APP_API_KEY },
    }),
};


function readHeadersAndParams() {
  const domainId = localStorage.getItem('domain_id');
  const secret = localStorage.getItem('secret');
  const urlParams = new URLSearchParams(window.location.search);
  const roleParam = urlParams.get('role');
  const idParam = urlParams.get('id');
  const secretParam = urlParams.get('secret');
  const domainIdParam = urlParams.get('domain_id');

  let token = null;
  let tokenType = null;

  if (secretParam) { token = secretParam; tokenType = 'SECRET-TOKEN'; }
  else if (secret) { token = secret; tokenType = 'SECRET-TOKEN'; }
  else if (domainIdParam) { token = domainIdParam; tokenType = 'DOMAIN-TOKEN'; }
  else if (domainId) { token = domainId; tokenType = 'DOMAIN-TOKEN'; }

  return {
    token,
    tokenType,
    role: roleParam || '',
    id: idParam || '',
    hasSpecialParams: !!(token || roleParam || idParam),
  };
}

async function performLogin({ email, password, type, rememberMe, decoded, signal }) {
  const { token, tokenType, role, id, hasSpecialParams } = readHeadersAndParams();

  const basePayload = decoded
    ? { mail: decoded.email, name: decoded.name, profile_pic: decoded.picture, type, remember_me: rememberMe }
    : { mail: email, password, type, remember_me: rememberMe };

  if (hasSpecialParams) {
    const headers = token && tokenType ? { [tokenType]: token } : {};
    const payload = { ...basePayload };
    if (role) payload.role = role;
    if (id) payload.id = id;
    return authApi.login(payload, headers, signal);
  }

  return authApi.reLogin(basePayload, signal);
}


export const useAuthFlow = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { initializeSession } = useSession();

  const [state, dispatch] = useReducer(authReducer, initialState);
  const { currentStep, loadingState, email, otp, password, rememberMe, snackbar } = state;

  const abortControllersRef = useRef({});

  const showSnackbar = useCallback((message, severity = 'success') => {
    dispatch({ type: 'SHOW_SNACKBAR', message, severity });
  }, []);

  const hideSnackbar = useCallback(() => dispatch({ type: 'HIDE_SNACKBAR' }), []);


  const resetAuthFlow = useCallback(() => {
    Object.values(abortControllersRef.current).forEach(c => c?.abort());
    abortControllersRef.current = {};
    dispatch({ type: 'RESET' });
  }, [showSnackbar]);

 
  const handleSuccessfulLogin = useCallback(async (result, userEmail) => {
    const domains = getDomainsFromResponse(result);
    const userRole = getUserRole(result);

    if (userRole) localStorage.setItem(STORAGE_KEYS.ROLE, userRole);
    if (result.access_token) setAccessToken(result.access_token);

    if (!localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true') {
      localStorage.setItem(STORAGE_KEYS.SHOW_STAY_LOGGED_IN_PROMPT, 'true');
    }

    const loginSuccess = login(
      {
        id: result.user_id,
        email: userEmail,
        name: result.name,
        profile_pic: result.profile_pic || null,
        is_super_admin: isSuperAdmin(result.is_super_admin),
        is_whitelabel_user: result.is_whitelabel_user,
        is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
        user_token: result.secret_token || null,
        access_token: result.access_token,
      },
      domains,
    );

    if (!loginSuccess) {
      showSnackbar('Login failed. Please try again.', 'error');
      return false;
    }

    showSnackbar('Login Successful! Redirecting...', 'success');

   const oauthResult = await completePendingOAuth(result.user_token);

    if (oauthResult?.success) {
      window.location.href = oauthResult.redirectUrl;
      return true;
    }

    if (oauthResult?.message) {
        sessionStorage.setItem("oauth_error", "true");
      showSnackbar(oauthResult.message, "error");
      return false
    }

    const isSuperAdminUser = isSuperAdmin(result.is_super_admin);
    const hasDomains = domains?.length > 0;
    const isReadOnly = hasDomains && domains[0].role === 'read only';

    if (isSuperAdminUser) {
      setTimeout(() => navigate('/customers'), 500);
    } else if (!hasDomains) {
      dispatch({ type: 'SET_STEP', payload: 'registration' });
    } else if (isReadOnly) {
      setTimeout(() => navigate('/dashboard'), 500);
    } else {

      const installSource = getInstallSource();
      if (installSource === 'hubspot_marketplace') {
        localStorage.removeItem(STORAGE_KEYS.INSTALL_SOURCE);
        setTimeout(() => navigate('/integrations?tab=hubspot'), 500);
      } else {
        setTimeout(() => navigate('/settings'), 500);
      }
    }

    return true;
  }, [login, navigate, showSnackbar]);


  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.EMAIL });
    const controller = new AbortController();
    abortControllersRef.current.email = controller;

    try {
      await authApi.signup(email, controller.signal);
      dispatch({ type: 'SET_STEP', payload: 'otp' });
      showSnackbar('Verification code sent to your email!', 'success');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (error.response?.status === 409) {
        dispatch({ type: 'SET_STEP', payload: 'password' });
      } else {
        showSnackbar(error.response?.data?.messages?.[0] ?? 'Signup check failed. Please try again.', 'error');
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
      delete abortControllersRef.current.email;
    }
  }, [email, showSnackbar]);


  const handleOtpSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) {
      showSnackbar('Please enter a valid OTP', 'error');
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.OTP });
    const controller = new AbortController();
    abortControllersRef.current.otp = controller;

    try {
      await authApi.verifyOtp(email, otp, controller.signal);
      showSnackbar('Email verified successfully!', 'success');
      dispatch({ type: 'SET_STEP', payload: 'signup' });
    } catch (error) {
      if (error.name === 'AbortError') return;
      showSnackbar(error.response?.data?.messages?.[0] ?? 'OTP verification failed. Please try again.', 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
      delete abortControllersRef.current.otp;
    }
  }, [email, otp, showSnackbar]);

  const handlePasswordLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!password) {
      showSnackbar('Please enter your password', 'error');
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.PASSWORD });
    const controller = new AbortController();
    abortControllersRef.current.password = controller;

    try {
      const response = await performLogin({ email, password, type: 'custom', rememberMe, signal: controller.signal });

      if (response.status === 200 && response.data?.result) {
        window.history.replaceState({}, '', window.location.pathname);
        localStorage.removeItem('secret');
        localStorage.removeItem('domain_id');
        await handleSuccessfulLogin(response.data.result, email);
        initializeSession(rememberMe)
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      const msg = error.response?.status === 401
        ? 'Invalid email or password. Please try again.'
        : (error.response?.data?.messages?.[0] ?? 'Invalid credentials. Please try again.');
      showSnackbar(msg, 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
      delete abortControllersRef.current.password;
    }
  }, [email, password, rememberMe, handleSuccessfulLogin, showSnackbar]);

  const handleForgotPassword = useCallback(async (forgotEmail) => {
    if (!validateEmail(forgotEmail)) {
      showSnackbar('Please enter a valid email address', 'error');
      return { success: false, message: 'Invalid email' };
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.FORGOT_PASSWORD });

    try {
      await authApi.resetPassword(forgotEmail);
      showSnackbar('Reset link sent to your email!', 'success');
      return { success: true, message: 'Reset link sent successfully' };
    } catch (err) {
      const msg = err.response?.data?.messages?.[0] ?? "Failed to send reset link. Email doesn't exist.";
      showSnackbar(msg, 'error');
      return { success: false, message: msg };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
    }
  }, [showSnackbar]);

  const handleGoogleSignIn = useCallback(async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      showSnackbar('Google sign-in failed. Invalid response.', 'error');
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.GOOGLE });
    const controller = new AbortController();
    abortControllersRef.current.google = controller;

    try {
      const decoded = jwtDecode(credentialResponse.credential);
      let response;
      try {
        response = await performLogin({
          type: 'google',
          rememberMe,
          decoded,
          signal: controller.signal,
        });
      } catch (loginError) {
        if (loginError.response?.status !== 401) throw loginError;
        showSnackbar('Creating new account...', 'info');
        await authApi.saveGoogleUser(
          decoded,
          localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE),
          controller.signal,
        );
        showSnackbar('Account created successfully!', 'success');

        response = await performLogin({
          type: 'google',
          rememberMe,
          decoded,
          signal: controller.signal,
        });
      }

      if (response?.status === 200 && response.data?.result) {
        const { result } = response.data;
        const domains = getDomainsFromResponse(result);

        initializeSession(rememberMe);
        window.history.replaceState({}, '', window.location.pathname);
        localStorage.removeItem('secret');
        localStorage.removeItem('domain_id');
        localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);

        if (domains.length === 0) {
          dispatch({ type: 'SET_EMAIL', payload: decoded.email });
          dispatch({ type: 'SET_STEP', payload: 'registration' });
          showSnackbar('Please register your domain to continue.', 'info');
          login(
            {
              id: result.user_id,
              email: decoded.email,
              name: result.name,
              profile_pic: result.profile_pic || decoded.picture || null,
              is_super_admin: isSuperAdmin(result.is_super_admin),
              is_whitelabel_user: result.is_whitelabel_user,
              user_token: result.secret_token || null,
              access_token: result.access_token,
            },
            domains,
          );
          if (result.access_token) setAccessToken(result.access_token);
        } else {
          await handleSuccessfulLogin(result, decoded.email);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      showSnackbar(error.response?.data?.messages?.[0] ?? 'Failed to login with Google. Please try again.', 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
      delete abortControllersRef.current.google;
    }
  }, [handleSuccessfulLogin, initializeSession, login, showSnackbar, rememberMe]);


  const handleSignupSubmit = useCallback(async (signupData) => {
    if (!signupData.password || !signupData.firstName || !signupData.lastName) {
      showSnackbar('Please fill in all required fields', 'error');
      return { success: false };
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.SIGNUP });

    try {
      const userName = `${signupData.firstName} ${signupData.lastName}`.trim();

      await authApi.savePassword(
        email,
        signupData.password,
        userName,
        localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE),
      );
      showSnackbar('Account created successfully!', 'success');
      localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);

      const loginResponse = await performLogin({
        email,
        password: signupData.password,
        type: 'custom',
        rememberMe,
      });

      if (loginResponse.status === 200 && loginResponse.data?.result) {
        const { result } = loginResponse.data;
        const userRole = getUserRole(result);

        if (userRole) localStorage.setItem(STORAGE_KEYS.ROLE, userRole);
        if (result.access_token) setAccessToken(result.access_token);

        initializeSession(rememberMe);
        window.history.replaceState({}, '', window.location.pathname);
        localStorage.removeItem('secret');
        localStorage.removeItem('domain_id');

        login(
          {
            id: result.user_id,
            email,
            name: userName,
            is_super_admin: isSuperAdmin(result.is_super_admin),
            is_whitelabel_user: result.is_whitelabel_user,
            access_token: result.access_token,
          },
          getDomainsFromResponse(result),
        );
        dispatch({ type: 'SET_STEP', payload: 'registration' });
        showSnackbar('Please complete domain registration', 'info');
        return { success: true };
      }
    } catch (err) {
      showSnackbar('Failed to create account! Please try again.', 'error');
      return { success: false, error: err };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
    }
  }, [email, login, initializeSession, showSnackbar, rememberMe]);


  const handleRegistrationSubmit = useCallback(async (registrationData) => {
    if (!registrationData.cleanDomain || !registrationData.websitePlatform || !registrationData.companyName) {
      showSnackbar('Please fill in all fields', 'error');
      return { success: false };
    }

    dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.REGISTRATION });

    try {
      const { cleanDomain, websitePlatform, companyName } = registrationData;

      await authApi.saveRegistration({
        domain_url: cleanDomain,
        source: websitePlatform,
        email,
        first_name: user?.name?.split(' ')[0] || '',
        last_name: user?.name?.split(' ')[1] || '',
      });

      const PLUGIN_PLATFORMS = ['wordpress', 'webflow'];
      const DIRECT_PLATFORMS = ['wix', 'squarespace', 'custom'];

      if (PLUGIN_PLATFORMS.includes(websitePlatform)) {
        const pluginUrl = websitePlatform === 'wordpress'
          ? 'https://wordpress.org/plugins/ansera-search'
          : 'https://webflow.com/apps/detail/ansera-search';

        const hasPendingOAuth = !!sessionStorage.getItem('pending_oauth');

        if (hasPendingOAuth) {
          sessionStorage.removeItem('pending_oauth');
          showSnackbar('Complete the plugin installation, then connect again from Claude.', 'info');
        } else {
          showSnackbar('Thank you for registering with Ansera! Redirecting to plugin...', 'success');
          localStorage.setItem('justRedirected', 'true');
        }

        setTimeout(() => { window.location.href = pluginUrl; }, hasPendingOAuth ? 4000 : 3000);
        return { success: true };
      }

      if (DIRECT_PLATFORMS.includes(websitePlatform)) {
        const response = await authApi.register({
          domain: cleanDomain,
          source: websitePlatform,
          company_name: companyName,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          date_formate: 'MM-DD-YYYY',
          email,
          user_name: user?.name || '',
          is_google_user: false,
          is_user_logged_in: false,
        });

        if (response.status !== 200) return { success: false };

        showSnackbar('Registration successful! Redirecting...', 'success');
        localStorage.setItem('role', 'admin');
        const loginType = user?.profile_pic ? 'google' : 'custom';
        const reLoginResponse = await authApi.reLogin({
          mail: email,
          type: loginType,
          ...(loginType === 'custom' && { password }),
        });

        if (reLoginResponse.status === 200 && reLoginResponse.data?.result) {
          const { result } = reLoginResponse.data;

          if (result.access_token) setAccessToken(result.access_token);

          login(
            {
              id: result.user_id,
              email,
              name: result.name || user?.name,
              profile_pic: result.profile_pic || user?.profile_pic || null,
              is_super_admin: isSuperAdmin(result.is_super_admin),
              is_whitelabel_user: result.is_whitelabel_user,
              is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
              user_token: result.user_token || null,
              access_token: result.access_token,
            },
            getDomainsFromResponse(result),
          );
          await handleSuccessfulLogin(result, email);
          console.log(response.data?.result?.user_token, user?.user_token);
    
          const userTokenForOAuth = response.data?.result?.user_token || user?.user_token;
          //console.log('User token for OAuth completion:', userTokenForOAuth);
         const oauthResult = await completePendingOAuth(userTokenForOAuth);

          if (oauthResult?.success) {
            return { success: true };
          }
          
          if (oauthResult?.message) {
            showSnackbar(oauthResult.message, "error");
            return { success: false };
          }

          const installSource = getInstallSource();
          setTimeout(() => {
            if (installSource === 'hubspot_marketplace') {
              localStorage.removeItem(STORAGE_KEYS.INSTALL_SOURCE);
              navigate('/integrations?tab=hubspot');
            } else {
              navigate('/settings');
            }
          }, 2000);
        }
      }

      return { success: true };
    } catch (err) {
      showSnackbar('Domain registration failed. Please try again.', 'error');
      return { success: false, error: err };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: LOADING_STATES.IDLE });
    }
  }, [email, password, user, login, navigate, showSnackbar, handleSuccessfulLogin]);

  useEffect(() => {
    return () => {
      Object.values(abortControllersRef.current).forEach(c => c?.abort());
    };
  }, []);


  return {
    currentStep,
    setCurrentStep: (step) => dispatch({ type: 'SET_STEP', payload: step }),
    loadingState,
    email,
    setEmail: (v) => dispatch({ type: 'SET_EMAIL', payload: v }),
    otp,
    setOtp: (v) => dispatch({ type: 'SET_OTP', payload: v }),
    password,
    setPassword: (v) => dispatch({ type: 'SET_PASSWORD', payload: v }),
    rememberMe,
    setRememberMe: (v) => dispatch({ type: 'SET_REMEMBER_ME', payload: v }),
    snackbar,
    showSnackbar,
    hideSnackbar,
    resetAuthFlow,
    handleEmailSubmit,
    handleOtpSubmit,
    handlePasswordLogin,
    handleForgotPassword,
    handleGoogleSignIn,
    handleSignupSubmit,
    handleRegistrationSubmit,
    isAuthenticated: !!user,
    userEmail: user?.email ?? '',
    userName: user?.name ?? '',
  };
};